(() => {
  'use strict';

  const defaultRoutes = {
    cart: '/cart',
    cartAdd: '/cart/add.js',
    cartChange: '/cart/change.js',
    cartDrawer: '/cart?view=cart-drawer'
  };

  const defaultThemeData = {
    currency: 'EUR',
    productVariants: [],
    routes: defaultRoutes
  };
  let initialized = false;

  const readThemeData = () => {
    const node = document.getElementById('MixedownThemeData');
    if (!node) return defaultThemeData;

    try {
      const data = JSON.parse(node.textContent);
      return {
        currency: data.currency || 'EUR',
        productVariants: Array.isArray(data.productVariants) ? data.productVariants : [],
        routes: Object.assign({}, defaultRoutes, data.routes || {})
      };
    } catch (error) {
      return defaultThemeData;
    }
  };

  const themeData = readThemeData();
  const { scrollLock } = window.MixedownUI;
  const elements = {};
  const selectedOptions = {};
  let isAddingToCart = false;
  let cartDrawerRequest = null;
  let cartDrawerRefreshQueued = false;
  let cartDrawerAnimationFrame = null;
  let cartDrawerReturnFocus = null;
  let cartDrawerPrefetchTimer = null;
  let addToCartDefaultContent = null;
  const fetchTimeout = 12000;
  const priceFormatter = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
    style: 'currency',
    currency: themeData.currency || 'EUR'
  });
  const isElement = value => value instanceof Element;
  const getPathname = value => {
    try {
      return new URL(value, window.location.origin).pathname;
    } catch (error) {
      return '';
    }
  };

  const initializeElements = () => {
    elements.cartDrawer = document.getElementById('CartDrawer');
    elements.cartDrawerOverlay = document.getElementById('CartDrawerOverlay');
    elements.cartDrawerContent = document.getElementById('CartDrawerContent');
    elements.closeCartDrawerButton = document.getElementById('CloseCartDrawer');
    elements.addToCartForm = document.getElementById('AddToCartForm');
    elements.addToCartButton = document.querySelector('.product-info__cart-btn');
    elements.optionButtons = Array.from(document.querySelectorAll('.option-value'));
  };

  const safeFetch = async (url, options = {}, retries = 1, timeout = fetchTimeout) => {
    const requestUrl = new URL(url, window.location.origin);
    if (requestUrl.origin !== window.location.origin) throw new Error('Invalid request origin');

    for (let attempt = 0; attempt <= retries; attempt += 1) {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), timeout);
      try {
        const response = await fetch(requestUrl.toString(), Object.assign({}, options, { credentials: 'same-origin', signal: controller.signal }));
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response;
      } catch (error) {
        if (attempt === retries) throw error;
        await new Promise(resolve => window.setTimeout(resolve, 350 * (attempt + 1)));
      } finally {
        window.clearTimeout(timer);
      }
    }
  };

  const isCartDrawerOpen = () => Boolean(elements.cartDrawer && elements.cartDrawer.classList.contains('open'));

  const openCartDrawer = () => {
    if (!elements.cartDrawer || !elements.cartDrawerOverlay || isCartDrawerOpen()) return;
    if (cartDrawerAnimationFrame) window.cancelAnimationFrame(cartDrawerAnimationFrame);
    const activeElement = document.activeElement;
    if (activeElement && !elements.cartDrawer.contains(activeElement)) cartDrawerReturnFocus = activeElement;
    elements.cartDrawer.classList.add('open');
    elements.cartDrawerOverlay.classList.add('fade-in-drawer');
    elements.cartDrawer.inert = false;
    elements.cartDrawer.setAttribute('aria-hidden', 'false');
    elements.cartDrawerOverlay.setAttribute('aria-hidden', 'false');
    scrollLock.lock();
    cartDrawerAnimationFrame = window.requestAnimationFrame(() => {
      if (isCartDrawerOpen()) elements.cartDrawerOverlay.classList.add('active');
      cartDrawerAnimationFrame = null;
    });
    if (elements.closeCartDrawerButton) elements.closeCartDrawerButton.focus({ preventScroll: true });
  };

  const closeCartDrawer = () => {
    if (!elements.cartDrawerOverlay || !isCartDrawerOpen()) return;
    if (cartDrawerAnimationFrame) {
      window.cancelAnimationFrame(cartDrawerAnimationFrame);
      cartDrawerAnimationFrame = null;
    }
    // Move focus out before hiding, otherwise the browser blocks aria-hidden on the focused ancestor.
    if (elements.cartDrawer.contains(document.activeElement)) {
      if (cartDrawerReturnFocus && cartDrawerReturnFocus.isConnected) cartDrawerReturnFocus.focus({ preventScroll: true });
      else document.activeElement.blur();
    }
    cartDrawerReturnFocus = null;
    elements.cartDrawer.classList.remove('open');
    elements.cartDrawerOverlay.classList.remove('fade-in-drawer', 'active');
    elements.cartDrawer.inert = true;
    elements.cartDrawer.setAttribute('aria-hidden', 'true');
    elements.cartDrawerOverlay.setAttribute('aria-hidden', 'true');
    scrollLock.unlock();
  };

  const setAddToCartLoading = isLoading => {
    if (!elements.addToCartButton) return;

    if (isLoading) {
      if (!addToCartDefaultContent) {
        addToCartDefaultContent = Array.from(elements.addToCartButton.childNodes).map(node => node.cloneNode(true));
      }
      elements.addToCartButton.classList.add('is-loading');
      elements.addToCartButton.disabled = true;
      elements.addToCartButton.textContent = 'Hinzufügen...';
      return;
    }

    elements.addToCartButton.classList.remove('is-loading');
    elements.addToCartButton.disabled = false;

    if (addToCartDefaultContent) {
      elements.addToCartButton.replaceChildren(...addToCartDefaultContent);
      addToCartDefaultContent = null;
    }
  };

  const isCartDrawerContentEmpty = () => {
    if (!elements.cartDrawerContent) return true;
    return elements.cartDrawerContent.childElementCount === 0;
  };

  const setCartDrawerLoading = () => {
    if (!elements.cartDrawerContent || !isCartDrawerContentEmpty()) return;
    elements.cartDrawerContent.replaceChildren(Object.assign(document.createElement('div'), {
      className: 'cart-drawer__loading',
      textContent: 'Lädt...',
      ariaLive: 'polite'
    }));
  };

  const refreshCartDrawerContent = () => {
    if (!elements.cartDrawerContent) return Promise.resolve();
    if (cartDrawerRequest) {
      cartDrawerRefreshQueued = true;
      return cartDrawerRequest;
    }

    cartDrawerRequest = safeFetch(themeData.routes.cartDrawer)
      .then(response => response.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newContent = doc.getElementById('CartDrawerContent');
        if (!newContent) return;
        elements.cartDrawerContent.replaceChildren(...Array.from(newContent.childNodes));
      })
      .finally(() => {
        cartDrawerRequest = null;
        if (!cartDrawerRefreshQueued) return undefined;
        cartDrawerRefreshQueued = false;
        return refreshCartDrawerContent();
      });

    return cartDrawerRequest;
  };

  const prefetchCartDrawer = () => {
    if (!elements.cartDrawerContent || isCartDrawerOpen()) return;
    if (cartDrawerPrefetchTimer) window.clearTimeout(cartDrawerPrefetchTimer);
    cartDrawerPrefetchTimer = window.setTimeout(() => {
      cartDrawerPrefetchTimer = null;
      refreshCartDrawerContent().catch(() => {});
    }, 80);
  };

  const handleQuantityChange = async button => {
    const key = button.dataset.key;
    const quantity = Number(button.dataset.quantity);
    const cartItem = button.closest('.cart-drawer-item');
    if (!key || !Number.isInteger(quantity) || quantity < 0 || button.disabled) return;
    const buttons = cartItem ? Array.from(cartItem.querySelectorAll('button[data-key]')) : [button];
    buttons.forEach(item => { item.disabled = true; });
    if (quantity === 0 && cartItem) cartItem.classList.add('fade-out-bck');

    try {
      await safeFetch(themeData.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      }, 0);
    } catch (error) {
      // The refresh below shows what the cart actually holds, e.g. when stock ran out.
    }

    try {
      await refreshCartDrawerContent();
    } catch (error) {
      buttons.forEach(item => { item.disabled = false; });
      if (cartItem) cartItem.classList.remove('fade-out-bck');
    }
  };

  const findMatchingVariant = () => themeData.productVariants.find(variant =>
    Object.keys(selectedOptions).every(option => variant[option] === selectedOptions[option]) && variant.available
  );

  const formatPrice = priceInCents => {
    const amount = Number(priceInCents);
    return priceFormatter.format(Number.isFinite(amount) ? amount / 100 : 0);
  };

  const updateActiveStates = () => {
    elements.optionButtons.forEach(button => {
      const optionIndex = button.dataset.option;
      const isActive = selectedOptions[`option${optionIndex}`] === button.dataset.value;
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });
  };

  const updateOptionsAvailability = () => {
    if (!elements.addToCartButton || elements.optionButtons.length === 0) return;

    if (!themeData.productVariants.some(variant => variant.available)) {
      elements.addToCartButton.textContent = 'AUSVERKAUFT';
      elements.addToCartButton.disabled = true;
      elements.optionButtons.forEach(button => {
        button.classList.add('disabled');
        button.setAttribute('aria-disabled', 'true');
      });
      return;
    }

    const selectedOptionsEntries = Object.entries(selectedOptions);

    elements.optionButtons.forEach(button => {
      const optionIndex = button.dataset.option;
      const value = button.dataset.value;
      const isAvailable = themeData.productVariants.some(variant => {
        if (!variant.available) return false;
        return selectedOptionsEntries.every(([key, selectedValue]) =>
          key === `option${optionIndex}` || variant[key] === selectedValue
        ) && variant[`option${optionIndex}`] === value;
      });

      button.classList.toggle('disabled', !isAvailable);
      button.setAttribute('aria-disabled', String(!isAvailable));
    });
  };

  const updateVariantAndPrice = () => {
    const matchingVariant = findMatchingVariant();
    if (!matchingVariant || !elements.addToCartButton) return;

    elements.addToCartButton.disabled = false;
    elements.addToCartButton.type = 'submit';
    elements.addToCartButton.textContent = `In den Warenkorb - ${formatPrice(matchingVariant.price)}`;

    if (elements.addToCartForm) {
      const idInput = elements.addToCartForm.querySelector('input[name="id"]');
      if (idInput) idInput.value = matchingVariant.id;
    }
  };

  const initializeSelections = () => {
    if (themeData.productVariants.length === 0 || elements.optionButtons.length === 0) return;

    const idInput = elements.addToCartForm && elements.addToCartForm.querySelector('input[name="id"]');
    const initialVariant = themeData.productVariants.find(variant => variant.available && idInput && String(variant.id) === idInput.value)
      || themeData.productVariants.find(variant => variant.available);
    if (!initialVariant) return;

    initialVariant.options.forEach((optionValue, index) => {
      selectedOptions[`option${index + 1}`] = optionValue;
    });

    window.requestAnimationFrame(() => {
      updateActiveStates();
      updateOptionsAvailability();
      updateVariantAndPrice();
    });
  };

  const handleAddToCart = async event => {
    event.preventDefault();

    const idInput = elements.addToCartForm && elements.addToCartForm.querySelector('input[name="id"]');
    if (!idInput || !idInput.value || isAddingToCart) return;

    isAddingToCart = true;
    setAddToCartLoading(true);

    try {
      await safeFetch(themeData.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: idInput.value, quantity: 1 })
      });

      openCartDrawer();
      setCartDrawerLoading();
      await refreshCartDrawerContent();
    } catch (error) {
      // Add-to-cart failed; the button is restored in finally.
    } finally {
      isAddingToCart = false;
      setAddToCartLoading(false);
    }
  };

  const handleCartDrawerOpenRequest = () => {
    openCartDrawer();
    setCartDrawerLoading();
    refreshCartDrawerContent().catch(() => {});
  };

  const handleDocumentClick = event => {
    const target = event.target;
    if (!isElement(target)) return;
    const optionButton = target.closest('.option-value');
    const cartLink = target.closest('a[href]');
    const quantityButton = target.closest('.cart-drawer-item button[data-quantity]');

    if (target === elements.cartDrawerOverlay || target.closest('#CloseCartDrawer')) {
      closeCartDrawer();
      return;
    }

    if (quantityButton) {
      event.preventDefault();
      handleQuantityChange(quantityButton);
      return;
    }

    if (optionButton && !optionButton.classList.contains('disabled') && !isAddingToCart) {
      selectedOptions[`option${optionButton.dataset.option}`] = optionButton.dataset.value;
      updateActiveStates();
      updateOptionsAvailability();
      updateVariantAndPrice();
      return;
    }

    if (cartLink && getPathname(cartLink.href) === getPathname(themeData.routes.cart)) {
      event.preventDefault();
      handleCartDrawerOpenRequest();
      return;
    }

    if (isCartDrawerOpen() && !elements.cartDrawer.contains(target)) {
      closeCartDrawer();
    }
  };

  const initZoomPrevention = () => {
    document.addEventListener('gesturestart', event => event.preventDefault(), { passive: false });
    document.addEventListener('gesturechange', event => event.preventDefault(), { passive: false });
    document.addEventListener('gestureend', event => event.preventDefault(), { passive: false });
    if ('ontouchstart' in window) {
      document.addEventListener('dblclick', event => event.preventDefault(), { passive: false });
    }
  };

  const init = () => {
    if (initialized) return;
    initialized = true;
    initZoomPrevention();
    initializeElements();
    initializeSelections();
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && isCartDrawerOpen()) closeCartDrawer();
    });
    document.addEventListener('mouseover', event => {
      const target = event.target;
      if (!isElement(target)) return;
      const cartLink = target.closest('a[href]');
      if (cartLink && getPathname(cartLink.href) === getPathname(themeData.routes.cart)) prefetchCartDrawer();
    });
    if (elements.addToCartForm) elements.addToCartForm.addEventListener('submit', handleAddToCart);
    if (document.querySelector('[data-open-cart-on-load]')) handleCartDrawerOpenRequest();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
